import Text from '@app/components/ui/Text/Text';
import { currencyFormatter, SupportedCurrencies } from '@app/utils/pricingCurrencyUtils';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FinanceIncomeExpensesFormattedRow } from './types';

type IncomeVsExpensesTableProps = {
  rows: FinanceIncomeExpensesFormattedRow[];
  currency: SupportedCurrencies;
  locale: string;
  monthHeader: string;
};

const IncomeVsExpensesTable = ({ rows, currency, locale, monthHeader }: IncomeVsExpensesTableProps) => {
  const columns: ColumnsType<FinanceIncomeExpensesFormattedRow & { totalRevenue: number }> = [
    {
      title: <Text type="label2" untranslatedText={monthHeader} noWrap fw={700} />,
      dataIndex: 'monthLabel',
      key: 'month',
      width: 140,
      render: (value: string) => <Text type="body2" untranslatedText={value} color="#334155" noWrap />,
    },
    {
      title: <Text type="label2" transKey="income-capital" fw={700} />,
      dataIndex: 'income',
      key: 'income',
      align: 'right',
      render: (value: number) => (
        <Text
          type="body2"
          untranslatedText={currencyFormatter(Math.abs(value), currency, locale)}
          className="text-emerald-600"
        />
      ),
    },
    {
      title: <Text type="label2" transKey="doctors-capital" fw={700} />,
      dataIndex: 'doctors',
      key: 'doctors',
      align: 'right',
      render: (value: number) => (
        <Text
          type="body2"
          untranslatedText={currencyFormatter(Math.abs(value), currency, locale)}
          className="text-slate-500"
        />
      ),
    },
    {
      title: <Text type="label2" transKey="caregivers-capital" fw={700} />,
      dataIndex: 'caregivers',
      key: 'caregivers',
      align: 'right',
      render: (value: number) => (
        <Text
          type="body2"
          untranslatedText={currencyFormatter(Math.abs(value), currency, locale)}
          className="text-sky-500"
        />
      ),
    },
    {
      title: <Text type="label2" transKey="profit-capital" fw={700} />,
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      align: 'right',
      render: (value: number) => {
        const revenueTotal = value ?? 0;
        const totalColor = revenueTotal < 0 ? '#be123c' : '#16a34a';

        return (
          <Text
            type="body2"
            untranslatedText={currencyFormatter(Math.abs(revenueTotal), currency, locale)}
            color={totalColor}
          />
        );
      },
    },
  ];

  const tableData = rows.map((row) => ({
    ...row,
    totalRevenue: row.income - row.doctors - row.caregivers,
  }));

  return (
    <Table
      columns={columns}
      dataSource={tableData}
      pagination={false}
      size="middle"
      rowKey="key"
      className="w-full [&_.ant-table]:!rounded-none [&_.ant-table-container]:!rounded-none [&_.ant-table-content]:!rounded-none [&_.ant-table-thead>tr>th]:!rounded-none [&_.ant-table-thead>tr>th:first-child]:!rounded-tl-none [&_.ant-table-thead>tr>th:last-child]:!rounded-tr-none [&_.ant-table-thead>tr>th]:!px-12 [&_.ant-table-tbody>tr>td]:!px-12 [&_.ant-table-tbody>tr>td]:!py-4 [&_.ant-table-tbody>tr:last-child>td]:!pb-12"
    />
  );
};

export default IncomeVsExpensesTable;
